/**
 * Phase 6.5 — WebGPU graphics backend.
 *
 * Second renderer codebase (Option A from docs/webgpu-port-plan.md). Imports
 * EXCLUSIVELY from 'three/webgpu' so its THREE namespace stays disjoint from
 * the WebGL renderer's. No Mesh/Material/Object3D ever crosses the boundary.
 *
 * Current scope (step 5c): basic chunk rendering. Spawns 1 mesher worker,
 * forwards chunk lifecycle from the worldView, creates a `Mesh` per emitted
 * section geometry. Uses `MeshBasicNodeMaterial` with the block atlas as
 * `map` — sampling at vMapUv = identical baseline to MeshBasicMaterial on
 * WebGL. No tile-fract greedy re-projection yet (Phase 6.1 Stage 1 carries
 * the WebGL-side patch; that needs a TSL graph translation here).
 *
 * Not yet implemented: entities, skybox, signs, banners, heads, particles,
 * cursor block highlight, sounds, panorama (we just render the placeholder).
 */

import {
  Scene,
  PerspectiveCamera,
  Color,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Mesh,
  AmbientLight,
  DirectionalLight,
  MeshBasicNodeMaterial,
  NearestFilter,
  Texture,
  WebGPURenderer,
  REVISION,
  ColorManagement,
  LinearSRGBColorSpace,
  NoColorSpace,
} from 'three/webgpu'

// Match the WebGL backend's color pipeline: disable automatic sRGB
// management so the atlas texture is sampled as-is (no decoding) and
// fragments hit the framebuffer without color-space conversion.
// (graphicsBackend.ts:15 does the same for the WebGL bundle.)
ColorManagement.enabled = false
import { attribute, texture as tslTexture, uv as tslUv } from 'three/tsl'

import type {
  GraphicsBackend,
  GraphicsBackendLoader,
  GraphicsInitOptions,
  DisplayWorldOptions,
} from '../../../src/appViewer'
import { Vec3 } from 'vec3'
import { initMesherWorker, meshersSendMcData } from '../lib/mesherClient'
import { WebGPUSkyboxRenderer, DEFAULT_TEMPERATURE } from './skyboxRenderer'
import { WebGPUPanorama } from './panorama'
import { profiler } from '../../../src/perfProfiler'

const addCanvasToPage = (): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.id = 'viewer-canvas-webgpu'
  canvas.style.position = 'absolute'
  canvas.style.top = '0'
  canvas.style.left = '0'
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.imageRendering = 'pixelated'
  canvas.style.zIndex = '0'
  document.body.appendChild(canvas)
  return canvas
}

const createGraphicsBackend: GraphicsBackendLoader = (initOptions: GraphicsInitOptions) => {
  let canvas: HTMLCanvasElement | undefined
  let renderer: WebGPURenderer | undefined
  let scene: Scene | undefined
  let camera: PerspectiveCamera | undefined
  let chunkMaterial: MeshBasicNodeMaterial | undefined
  let atlasTexture: Texture | undefined
  let animFrame = 0
  let rendering = true
  let initPromise: Promise<void> | undefined

  // Chunk-rendering state (step 5c)
  const workers: Worker[] = []
  const sectionObjects: Record<string, Mesh> = {}
  let worldStarted = false
  let currentDisplayOptions: DisplayWorldOptions | undefined
  let skybox: WebGPUSkyboxRenderer | undefined
  let panorama: WebGPUPanorama | undefined

  // Entity placeholder rendering — minimal port: one colored BoxGeometry per
  // entity sized to its collision box. Skin/animation/banner rendering is a
  // huge subsystem on the WebGL side (entities.ts is ~1500 lines) and not
  // in scope for the chunks-only milestone, but the user explicitly asked
  // for entities to be at least visible (carts through portals, etc.).
  const entityObjects: Record<number, Mesh> = {}
  const entityMaterials: Record<string, MeshBasicNodeMaterial> = {}
  const hashColor = (name: string): number => {
    let h = 0
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0
    // Map to a saturated mid-bright HSV color so different mob types are easy to tell apart.
    const hue = ((h >>> 0) % 360) / 360
    const s = 0.6, v = 0.85
    const i = Math.floor(hue * 6)
    const f = hue * 6 - i
    const p = v * (1 - s)
    const q = v * (1 - s * f)
    const t = v * (1 - s * (1 - f))
    let r = 0, gg = 0, bb = 0
    switch (i % 6) {
      case 0: r = v; gg = t; bb = p; break
      case 1: r = q; gg = v; bb = p; break
      case 2: r = p; gg = v; bb = t; break
      case 3: r = p; gg = q; bb = v; break
      case 4: r = t; gg = p; bb = v; break
      case 5: r = v; gg = p; bb = q; break
    }
    return (Math.round(r * 255) << 16) | (Math.round(gg * 255) << 8) | Math.round(bb * 255)
  }
  const getEntityMaterial = (name: string): MeshBasicNodeMaterial => {
    if (!entityMaterials[name]) {
      entityMaterials[name] = new MeshBasicNodeMaterial({ color: hashColor(name) })
    }
    return entityMaterials[name]
  }

  const init = async () => {
    if (initPromise) return initPromise
    initPromise = (async () => {
      canvas = addCanvasToPage()
      const w = window.innerWidth
      const h = window.innerHeight
      canvas.width = Math.round(w * devicePixelRatio)
      canvas.height = Math.round(h * devicePixelRatio)

      try {
        renderer = new WebGPURenderer({ canvas, antialias: false })
        await renderer.init()
        renderer.setSize(w, h, false)
        renderer.setPixelRatio(devicePixelRatio)
        // Match WebGL backend's output color space.
        renderer.outputColorSpace = LinearSRGBColorSpace
      } catch (err) {
        initOptions.callbacks.displayCriticalError(
          new Error(`Failed to initialize WebGPURenderer (${(err as Error).message}). Falling back to WebGL recommended.`)
        )
        throw err
      }

      scene = new Scene()
      scene.background = new Color(0x88_cc_ff)

      camera = new PerspectiveCamera(75, w / h, 0.1, 1000)
      camera.position.set(0, 70, 0)
      camera.lookAt(0, 65, -5)

      // No placeholder cube — clean sky background while waiting for chunks.
      // (Earlier iterations had an orange demo cube; it was useful for
      // architecture validation but ugly on the panorama / pre-connect view.)

      scene.add(new AmbientLight(0xff_ff_ff, 0.5))
      const dir = new DirectionalLight(0xff_ff_ff, 1.0)
      dir.position.set(1, 2, 1)
      scene.add(dir)

      const onResize = () => {
        if (!renderer || !camera) return
        const newW = window.innerWidth
        const newH = window.innerHeight
        camera.aspect = newW / newH
        camera.updateProjectionMatrix()
        renderer.setSize(newW, newH, false)
      }
      window.addEventListener('resize', onResize)

      // Use the renderer's own animation loop — it integrates with WebGPU's
      // frame timing (avoid back-to-back renderAsync queueing) better than
      // a manual rAF loop.
      let lastT = performance.now()
      renderer.setAnimationLoop(() => {
        if (!rendering || !renderer || !scene || !camera) return
        const now = performance.now()
        if (profiler.enabled) profiler.tick(now - lastT)
        lastT = now
        const _t0 = profiler.enabled ? now : 0
        drainPendingGeoms()
        if (panorama) panorama.tick()
        if (skybox) skybox.update(camera.position)
        const _trender = profiler.enabled ? performance.now() : 0
        renderer.renderAsync(scene, camera).then(() => {
          if (profiler.enabled) profiler.measure('webgpu.renderAsync', _trender)
        }).catch(err => {
          console.error('[webgpu] render error:', err)
        })
        if (profiler.enabled) profiler.measure('webgpu.mainFrame', _t0)
      })
    })()
    return initPromise
  }

  let geometryReceivedCount = 0
  let geometryEmptyCount = 0
  let lastDiagLog = 0

  // Pending-geometry queue. The mesher floods us in bursts (a fresh column
  // can emit 16+ section geometries in a single tick), and inserting all of
  // them into the scene synchronously on the main thread stalls the next
  // frame for ~30-50 ms (BufferGeometry attribute upload + Float32 color
  // conversion + WebGPU pipeline cache miss). We queue and drain on a fixed
  // time budget inside the animation loop, smoothing the 1% / 0.1% lows.
  const pendingGeoms: Array<{ key: string; g: any }> = []
  const FRAME_INSERT_BUDGET_MS = 3 // budget per frame for chunk inserts

  /** Take a single geometry payload from the queue and turn it into a Mesh. */
  const insertGeometry = (key: string, g: any) => {
    const _t0 = profiler.enabled ? performance.now() : 0
    if (sectionObjects[key]) {
      scene!.remove(sectionObjects[key]!)
      sectionObjects[key]!.geometry.dispose()
      delete sectionObjects[key]
    }
    if (!g || !g.positions || (g.positions as Float32Array).length === 0) {
      geometryEmptyCount++
      return
    }
    try {
      const geom = new BufferGeometry()
      const compact = (b: any) => !!b && !(b instanceof Float32Array)
      geom.setAttribute('position', new BufferAttribute(g.positions, 3))
      geom.setAttribute('normal', new BufferAttribute(g.normals, 3, compact(g.normals)))
      // Color attribute: convert Uint8-normalized (Phase 6.4) to Float32 here.
      // TSL's `attribute('vColor', 'vec3')` doesn't appear to read the
      // normalized=true flag on a Uint8 buffer in the current WebGPU path
      // (chunks render invisible). Float32 unblocks the per-vertex tint+AO
      // multiplication. ~4× more memory for colors, acceptable.
      let colorBuf: Float32Array
      if (g.colors instanceof Uint8Array) {
        colorBuf = new Float32Array(g.colors.length)
        for (let i = 0; i < g.colors.length; i++) colorBuf[i] = g.colors[i] / 255
      } else if (g.colors instanceof Int8Array) {
        colorBuf = new Float32Array(g.colors.length)
        for (let i = 0; i < g.colors.length; i++) colorBuf[i] = g.colors[i] / 127
      } else if (g.colors instanceof Float32Array) {
        colorBuf = g.colors
      } else {
        colorBuf = new Float32Array(g.colors)
      }
      geom.setAttribute('vColor', new BufferAttribute(colorBuf, 3))
      geom.setAttribute('uv', new BufferAttribute(g.uvs, 2, compact(g.uvs)))
      geom.setIndex(new BufferAttribute(g.indices as Uint32Array | Uint16Array, 1))
      const mesh = new Mesh(geom, chunkMaterial!)
      mesh.position.set(g.sx, g.sy, g.sz)
      scene!.add(mesh)
      sectionObjects[key] = mesh
      // Periodic diagnostic — log first section + every 100th
      if (geometryReceivedCount === 1 || geometryReceivedCount % 100 === 0) {
        const now = performance.now()
        if (now - lastDiagLog > 1000) {
          lastDiagLog = now
          console.log(`[webgpu] geometry #${geometryReceivedCount} at (${g.sx},${g.sy},${g.sz}) verts=${g.positions.length/3} idx=${g.indices.length} empty=${geometryEmptyCount} queued=${pendingGeoms.length}`)
        }
      }
    } catch (err) {
      console.error('[webgpu] mesh build failed:', (err as Error).message, 'key=', key)
    }
    if (profiler.enabled) profiler.measure('webgpu.insertChunk', _t0)
  }

  /** Drain at most FRAME_INSERT_BUDGET_MS ms of geometry inserts. */
  const drainPendingGeoms = () => {
    if (pendingGeoms.length === 0) return
    const _t0 = profiler.enabled ? performance.now() : 0
    const start = performance.now()
    while (pendingGeoms.length > 0 && performance.now() - start < FRAME_INSERT_BUDGET_MS) {
      const next = pendingGeoms.shift()!
      insertGeometry(next.key, next.g)
    }
    if (profiler.enabled) {
      profiler.measure('webgpu.drainPerFrame', _t0)
      profiler.sample('webgpu.pendingGeomQueue', pendingGeoms.length)
    }
  }

  /** Process a single batched message from a mesher worker. */
  const handleWorkerData = (data: any) => {
    if (Array.isArray(data)) {
      for (const m of data) handleWorkerData(m)
      return
    }
    if (data.type === 'geometry') {
      geometryReceivedCount++
      if (geometryReceivedCount === 1 && profiler.enabled) profiler.recordMilestone('firstChunk')
      // Queue rather than insert; drained by the animation loop on a budget.
      pendingGeoms.push({ key: data.key as string, g: data.geometry })
    } else if (data.type === 'sectionFinished') {
      // No-op for now.
    } else if (data.type === 'mc-web-pong') {
      // Worker alive — diagnostic only.
    }
  }

  const buildMesherConfig = (version: string) => ({
    version,
    enableLighting: true,
    skyLight: 15,
    smoothLighting: true,
    shadingTheme: 'high-contrast' as const,
    cardinalLight: 'default' as const,
    outputFormat: 'threeJs' as const, // mesher's data layout — same as WebGL path
    debugModelVariant: undefined as undefined | number[],
    clipWorldBelowY: undefined as undefined | number,
    disableBlockEntityTextures: true, // skip sign/banner geometry for now
    worldMinY: -64,
    worldMaxY: 320,
    greedyMesh: false,
  })

  const setupChunkPipeline = async (opts: DisplayWorldOptions) => {
    if (worldStarted) return
    worldStarted = true
    currentDisplayOptions = opts

    // Skybox + day-night cycle. The scene starts with a flat solid background,
    // but once the player joins a world we want the proper Minecraft sky
    // (gradient + fog + time-tinted void plane).
    if (scene) {
      const defaultSkyboxOn = opts.inWorldRenderingConfig?.defaultSkybox !== false
      skybox = new WebGPUSkyboxRenderer(scene, defaultSkyboxOn)
      skybox.updateTemperature(DEFAULT_TEMPERATURE)
    }

    const resources = (initOptions.resourcesManager as any).currentResources
    if (!resources) {
      console.warn('[webgpu] resources not ready, chunk material will be added once textures load')
    } else {
      atlasTexture = new Texture(resources.blocksAtlasImage)
      atlasTexture.needsUpdate = true
      atlasTexture.flipY = false
      atlasTexture.minFilter = NearestFilter
      atlasTexture.magFilter = NearestFilter
      // WebGL backend uses NoColorSpace effectively (ColorManagement off +
      // textures untagged). Match that to keep block colors identical.
      atlasTexture.colorSpace = NoColorSpace
    }

    // Diag modes via ?webgpuDiag=solid|noalpha|notint
    const diagFlag = typeof location !== 'undefined'
      ? new URLSearchParams(location.search).get('webgpuDiag')
      : null
    const diagSolid = diagFlag === 'solid'
    const diagNoAlpha = diagFlag === 'noalpha'
    const diagNoTint = diagFlag === 'notint'
    chunkMaterial = new MeshBasicNodeMaterial({
      // Don't use vertexColors:true — NodeMaterial's auto-handling doesn't play
      // nice with our Phase 6.4 Uint8-normalized color buffer. We hand-build
      // the TSL graph below.
      vertexColors: false,
      transparent: !diagSolid && !diagNoAlpha,
      alphaTest: (diagSolid || diagNoAlpha) ? 0 : 0.1,
      map: diagSolid ? null : (atlasTexture ?? null),
      color: diagSolid ? 0xff_00_ff : 0xff_ff_ff,
    })
    if (!diagSolid && atlasTexture) {
      // colorNode = texture × vertex color attribute (Float32 per-vertex
      // tint+AO, converted from the mesher's Uint8 source on the WebGPU
      // path — see vColor handling above).
      const sampled = tslTexture(atlasTexture, tslUv())
      if (diagNoTint) {
        chunkMaterial.colorNode = sampled
      } else {
        const tint = attribute('vColor', 'vec3')
        chunkMaterial.colorNode = sampled.mul(tint)
      }
    }

    // Pipeline pre-warm. WebGPU lazily compiles a pipeline the first time
    // a (material × geometry layout) combination is rendered, and the first
    // chunk arrival used to coincide with a ~30 ms compile stall on cold
    // sessions. Build a throwaway mesh with the chunk attribute layout
    // (position/normal/vColor/uv + indexed) and call `compileAsync` so the
    // GPU pipeline is ready before the user's first chunk lands.
    if (renderer && scene && camera && chunkMaterial) {
      try {
        const dummyGeom = new BufferGeometry()
        dummyGeom.setAttribute('position', new BufferAttribute(new Float32Array(9), 3))
        dummyGeom.setAttribute('normal', new BufferAttribute(new Float32Array(9), 3))
        dummyGeom.setAttribute('vColor', new BufferAttribute(new Float32Array(9), 3))
        dummyGeom.setAttribute('uv', new BufferAttribute(new Float32Array(6), 2))
        dummyGeom.setIndex(new BufferAttribute(new Uint16Array([0, 1, 2]), 1))
        const warm = new Mesh(dummyGeom, chunkMaterial)
        warm.visible = false
        scene.add(warm)
        await renderer.compileAsync(scene, camera)
        scene.remove(warm)
        dummyGeom.dispose()
      } catch (err) {
        console.warn('[webgpu] pipeline pre-warm failed (non-fatal):', (err as Error).message)
      }
    }

    // Spawn ONE mesher worker for now. The WebGL path uses N+1 — we keep
    // things minimal here while validating the pipeline.
    const worker = initMesherWorker(handleWorkerData)
    workers.push(worker)

    const config = buildMesherConfig(opts.version)
    meshersSendMcData(workers, opts.version, { config })

    // mesherData: hands the worker the atlas + block model registry.
    if (resources) {
      worker.postMessage({
        type: 'mesherData',
        workerIndex: 0,
        blocksAtlas: { latest: resources.blocksAtlasJson },
        blockstatesModels: resources.blockstatesModels,
        config,
      })
    }

    // Subscribe to worldView events.
    opts.worldView.on('loadChunk', ({ x, z, chunk }: any) => {
      for (const w of workers) {
        w.postMessage({ type: 'chunk', x, z, chunk })
      }
      for (let y = config.worldMinY; y < config.worldMaxY; y += 16) {
        for (const w of workers) {
          w.postMessage({ type: 'dirty', x, y, z, value: true })
        }
      }
    })

    opts.worldView.on('unloadChunk', ({ x, z }: { x: number, z: number }) => {
      for (const w of workers) {
        w.postMessage({ type: 'unloadChunk', x, z })
      }
      // Drop any still-queued inserts for this column — no point materializing
      // a mesh we're about to remove.
      for (let i = pendingGeoms.length - 1; i >= 0; i--) {
        const [sx, , sz] = pendingGeoms[i].key.split(',').map(Number)
        if (sx === x && sz === z) pendingGeoms.splice(i, 1)
      }
      // Remove any meshes belonging to this column.
      for (const key of Object.keys(sectionObjects)) {
        const [sx, , sz] = key.split(',').map(Number)
        if (sx === x && sz === z) {
          scene!.remove(sectionObjects[key]!)
          sectionObjects[key]!.geometry.dispose()
          delete sectionObjects[key]
        }
      }
    })

    const upsertEntity = (e: any) => {
      if (!scene) return
      if (e.delete) {
        const existing = entityObjects[e.id]
        if (existing) {
          scene.remove(existing)
          existing.geometry.dispose()
          delete entityObjects[e.id]
        }
        return
      }
      const pos = e.pos ?? e.position
      if (!pos) return
      let mesh = entityObjects[e.id]
      if (!mesh) {
        const w = Math.max(0.25, e.width ?? 0.6)
        const h = Math.max(0.25, e.height ?? 1.8)
        const geom = new BoxGeometry(w, h, w)
        // BoxGeometry is centered at origin — shift Y so the box base sits at the entity foot.
        geom.translate(0, h / 2, 0)
        mesh = new Mesh(geom, getEntityMaterial(e.name ?? 'unknown'))
        scene.add(mesh)
        entityObjects[e.id] = mesh
      }
      mesh.position.set(pos.x, pos.y, pos.z)
      if (typeof e.yaw === 'number') mesh.rotation.y = e.yaw
    }

    opts.worldView.on('entity', (e: any) => upsertEntity(e))
    opts.worldView.on('entityMoved', (e: any) => upsertEntity(e))

    opts.worldView.on('time', (timeOfDay: number) => {
      skybox?.updateTime(timeOfDay)
    })

    opts.worldView.on('renderDistance', (d: number) => {
      skybox?.updateViewDistance(d)
    })

    opts.worldView.on('biomeUpdate', ({ biome }: any) => {
      if (biome?.temperature !== undefined) skybox?.updateTemperature(biome.temperature)
    })

    opts.worldView.on('blockUpdate', ({ pos, stateId }: any) => {
      for (const w of workers) {
        w.postMessage({ type: 'blockUpdate', pos, stateId })
      }
      const sx = Math.floor(pos.x / 16) * 16
      const sy = Math.floor(pos.y / 16) * 16
      const sz = Math.floor(pos.z / 16) * 16
      for (const w of workers) {
        w.postMessage({ type: 'dirty', x: sx, y: sy, z: sz, value: true })
      }
    })

    console.log('[webgpu] chunk pipeline ready')
  }

  const startPanorama = async () => {
    await init()
    if (scene && camera && !panorama) {
      panorama = new WebGPUPanorama(scene, camera)
    }
  }

  const startWorld = async (opts: DisplayWorldOptions) => {
    await init()
    // Tear down the panorama box once the world starts streaming — its
    // 1000³ size would z-fight against chunks otherwise.
    if (panorama) {
      panorama.dispose()
      panorama = undefined
    }
    await setupChunkPipeline(opts)
    // Inform the user they're on the experimental backend.
    if (typeof document !== 'undefined') {
      const banner = document.createElement('div')
      banner.style.cssText = 'position:fixed;top:8px;right:8px;background:rgba(0,0,0,.65);color:#fff;padding:6px 10px;font:11px monospace;border:1px solid #f5d76e;border-radius:4px;z-index:50;pointer-events:none;max-width:280px;line-height:1.4'
      banner.textContent = 'WebGPU experimental — chunks only. No entities/skybox/signs/cursor yet. Switch back: ?renderer=threejs'
      document.body.appendChild(banner)
      setTimeout(() => banner.remove(), 12000)
    }
  }

  const disconnect = () => {
    if (renderer) renderer.setAnimationLoop(null)
    if (animFrame) cancelAnimationFrame(animFrame)
    animFrame = 0
    for (const w of workers) w.terminate()
    workers.length = 0
    for (const key of Object.keys(sectionObjects)) {
      sectionObjects[key]!.geometry.dispose()
      delete sectionObjects[key]
    }
    pendingGeoms.length = 0
    for (const id of Object.keys(entityObjects)) {
      const m = entityObjects[id as any]
      if (scene) scene.remove(m)
      m.geometry.dispose()
      delete entityObjects[id as any]
    }
    for (const k of Object.keys(entityMaterials)) {
      entityMaterials[k].dispose()
      delete entityMaterials[k]
    }
    if (skybox) {
      skybox.dispose()
      skybox = undefined
    }
    if (panorama) {
      panorama.dispose()
      panorama = undefined
    }
    if (chunkMaterial) {
      chunkMaterial.dispose()
      chunkMaterial = undefined
    }
    if (atlasTexture) {
      atlasTexture.dispose()
      atlasTexture = undefined
    }
    if (renderer) {
      renderer.dispose()
      renderer = undefined
    }
    if (canvas) {
      canvas.remove()
      canvas = undefined
    }
    scene = undefined
    camera = undefined
    initPromise = undefined
    worldStarted = false
    currentDisplayOptions = undefined
  }

  const backend: GraphicsBackend = {
    id: 'webgpu',
    displayName: `WebGPU (three.js ${REVISION})`,
    startPanorama,
    startWorld,
    disconnect,
    setRendering (next: boolean) {
      rendering = next
    },
    updateCamera (pos: Vec3 | null, yaw: number, pitch: number) {
      if (!camera || !pos) return
      // Mineflayer's bot.entity.yaw/pitch already match three.js camera
      // conventions: yaw=0 → looking -Z (which is "north" / default camera
      // direction), pitch=0 → horizontal, pitch>0 → up tilt. The WebGL
      // backend applies these directly via quaternions (see cameraShake.ts).
      // Initial port had +π and negated pitch — that's what inverted everything.
      camera.position.set(pos.x, pos.y + 1.62, pos.z)
      camera.rotation.order = 'YXZ'
      camera.rotation.set(pitch, yaw, 0)
      ;(globalThis as any).__webgpuCam = {
        pos: { x: pos.x.toFixed(1), y: pos.y.toFixed(1), z: pos.z.toFixed(1) },
        yaw: yaw.toFixed(2), pitch: pitch.toFixed(2),
        chunks: Object.keys(sectionObjects).length,
      }
    },
    soundSystem: undefined,
    backendMethods: undefined,
    getDebugOverlay: () => ({
      get info () {
        return `[webgpu] chunks=${Object.keys(sectionObjects).length} workers=${workers.length}`
      },
    }),
  }
  return backend
}

createGraphicsBackend.id = 'webgpu'

export default createGraphicsBackend
